import React, { useEffect, useState } from 'react';
import { ArrowRight, Download, Github, Linkedin, Mail } from 'lucide-react';
import { Button } from './ui/button';
import { portfolioData } from '../data/mock';
import { useTheme } from '../context/ThemeContext';
import heroBg from '../assets/hero-bg.jpeg';

const Hero = () => {
  const [isVisible, setIsVisible] = useState(false);
  const { personal } = portfolioData;
  const { theme } = useTheme();

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const handleDownloadResume = () => {
    const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
    window.location.href = `${BACKEND_URL}/api/resume/download`;
  };

  return (
    <section 
      id="home" 
      className="min-h-screen flex items-center justify-center pt-20 px-6 relative"
      style={{
        backgroundImage: `url(${heroBg})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat'
      }}
    >
      {/* Blur Overlay */}
      <div 
        className="absolute inset-0"
        style={{
          backdropFilter: 'blur(6px)',
          WebkitBackdropFilter: 'blur(6px)',
          backgroundColor: theme === 'dark' 
            ? 'rgba(0, 0, 0, 0.65)' 
            : 'rgba(255, 255, 255, 0.7)'
        }}
      ></div>
      
      <div className="container mx-auto relative z-10">
        <div className="grid md:grid-cols-2 gap-16 items-center max-w-6xl mx-auto">
          {/* Left Content */}
          <div
            className={`space-y-8 transform transition-all duration-1000 ${
              isVisible ? 'translate-x-0 opacity-100' : '-translate-x-10 opacity-0'
            }`}
          >
            <div className="inline-block">
              <span className="section-label">Portfolio</span>
            </div>

            <h1 className="text-6xl md:text-7xl lg:text-8xl font-bold leading-tight"
              style={{ color: 'var(--text-primary)' }}
            >
              <span className="block text-3xl md:text-4xl mb-4 opacity-0 animate-fade-in" style={{ 
                animationDelay: '0.2s',
                animationFillMode: 'forwards'
              }}>
                Hello!, I'm
              </span>
              <span className="opacity-0 animate-fade-in" style={{ 
                animationDelay: '0.5s',
                animationFillMode: 'forwards'
              }}>
                Prathamesh
              </span>
              <br />
              <span style={{ color: 'var(--accent)' }} className="opacity-0 animate-fade-in" style={{ 
                color: 'var(--accent)',
                animationDelay: '0.8s',
                animationFillMode: 'forwards'
              }}>
                Deshpande
              </span>
            </h1>

            <p className="text-2xl md:text-3xl text-gray-300 font-semibold" style={{ 
              color: 'var(--text-secondary)',
              fontSize: 'clamp(1.5rem, 3vw, 2rem)'
            }}>
              {personal.title}
            </p>

            <p className="text-xl text-gray-400 leading-relaxed max-w-xl" style={{ 
              color: 'var(--text-muted)', 
              fontFamily: 'JetBrains Mono, monospace',
              fontSize: 'clamp(1.125rem, 2vw, 1.5rem)'
            }}>
              {personal.tagline}
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-wrap gap-4 pt-4">
              <button
                onClick={() => scrollToSection('projects')}
                className="action-btn px-8 py-4 rounded-full font-medium flex items-center gap-2 group"
                style={{
                  background: 'var(--accent)',
                  color: '#000',
                  fontFamily: 'JetBrains Mono, monospace'
                }}
              >
                View Work
                <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
              </button>
              <button
                onClick={handleDownloadResume}
                className="action-btn-secondary px-8 py-4 rounded-full font-medium flex items-center gap-2 border"
                style={{
                  borderColor: 'var(--border)',
                  color: 'var(--text-primary)',
                  fontFamily: 'JetBrains Mono, monospace'
                }}
              >
                <Download className="h-5 w-5" />
                Resume
              </button>
            </div>

            {/* Social Links */}
            <div className="flex items-center gap-4 pt-4">
              {[
                { icon: Github, href: personal.github },
                { icon: Linkedin, href: personal.linkedin },
                { icon: Mail, href: `mailto:${personal.email}` }
              ].map((social, idx) => {
                const Icon = social.icon;
                return (
                  <a
                    key={idx}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="social-icon-btn"
                  >
                    <Icon className="h-5 w-5" />
                  </a>
                );
              })}
            </div>
          </div>

          {/* Right Content - Photo */}
          <div
            className={`relative transform transition-all duration-1000 delay-300 ${
              isVisible ? 'translate-x-0 opacity-100' : 'translate-x-10 opacity-0'
            }`}
          >
            <div className="relative w-full max-w-md mx-auto">
              {/* Image Container */}
              <div className="relative overflow-hidden rounded-3xl">
                <div className="absolute inset-0 border-4 rounded-3xl" style={{ borderColor: 'var(--accent)' }}></div>
                <img
                  src={personal.photo}
                  alt={personal.name}
                  className="relative w-full h-auto object-cover"
                />
              </div>

              {/* Floating Badge */}
              <div 
                className="absolute -bottom-4 -right-4 px-6 py-4 rounded-2xl border backdrop-blur-md"
                style={{
                  background: 'var(--bg-card)',
                  borderColor: 'var(--border)'
                }}
              >
                <div className="text-center">
                  <div className="text-3xl font-bold" style={{ color: 'var(--accent)' }}>2.5+</div>
                  <div className="text-sm" style={{ color: 'var(--text-muted)', fontFamily: 'JetBrains Mono, monospace' }}>Years</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
